import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { useCreatePerformanceMutation } from '~entities/recital-manager/api/create-performance';

import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '~shared/shadcn/ui/form';
import { Input } from '~shared/shadcn/ui/input';
import { Textarea } from '~shared/shadcn/ui/textarea';

const performanceFormSchema = z.object({
  name: z.string().min(1, 'Performance name is required'),
  duration: z.coerce.number().min(1, 'Duration must be at least 1 minute'),
  notes: z.string().optional(),
});

type PerformanceFormValues = z.infer<typeof performanceFormSchema>;

interface CreatePerformanceProps {
  actId: string;
  orderIndex: number;
  createDialogOpen: boolean;
  setCreateDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const CreatePerformance = ({
  actId,
  orderIndex = 0,
  createDialogOpen,
  setCreateDialogOpen,
}: CreatePerformanceProps) => {
  const form = useForm<PerformanceFormValues>({
    resolver: zodResolver(performanceFormSchema),
    defaultValues: {
      name: '',
      duration: undefined,
      notes: '',
    },
  });

  const { mutateAsync: createPerformance, isPending: isCreatingPerformance } =
    useCreatePerformanceMutation();

  const handleCreate = async (values: PerformanceFormValues) => {
    try {
      await createPerformance({
        actId,
        name: values.name,
        duration: values.duration,
        description: values.notes,
        orderIndex: orderIndex + 1,
        classIds: [],
        studentIds: [],
      });
      toast.success('Performance created successfully!');
      setCreateDialogOpen(false);
      form.reset();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create New Performance</DialogTitle>
          <DialogDescription>
            Add a new performance to this show
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleCreate)}
            className='space-y-4'
          >
            <FormField
              control={form.control}
              name='name'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Performance Name *</FormLabel>
                  <FormControl>
                    <Input placeholder='Ballet Performance' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='duration'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Duration (minutes) *</FormLabel>
                  <FormControl>
                    <Input type='number' min='1' placeholder='5' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='notes'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder='Any special notes for this performance...'
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type='button'
                variant='outline'
                onClick={() => {
                  setCreateDialogOpen(false);
                  form.reset();
                }}
              >
                Cancel
              </Button>
              <Button type='submit' disabled={isCreatingPerformance}>
                {isCreatingPerformance && <Loader2 className='animate-spin' />}
                Create Performance
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
